// frontend/src/pages/NewUILogin.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';

// Botón gaming reutilizable
function GamingButton({ variant = "secondary", className = "", leftIcon, children, disabled, ...props }) {
    const base =
        "inline-flex items-center justify-center gap-2.5 " +
        "h-12 md:h-14 px-6 md:px-8 " +
        "rounded-[14px] md:rounded-[16px] " +
        "font-bold tracking-wider leading-none " +
        "transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98] " +
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";

    const variants = {
        primary: "bg-accent text-secondary hover:bg-accent-light border-2 border-accent-dark",
        secondary: "bg-primary text-white hover:bg-primary-light border-2 border-primary-dark",
        ctaBlue: "bg-[#2284DF] text-white hover:bg-[#2A91F4] border-2 border-[#1E75C6]",
        ctaYellow: "bg-[#FCE503] text-[#122436] hover:bg-[#FCE503]/90 border-2 border-[#E5CF03]",
    };

    return (
        <button
            className={`${base} ${variants[variant]} ${className}`}
            disabled={disabled}
            {...props}
        >
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            <span className="whitespace-nowrap">{children}</span>
        </button>
    );
}

const NewUILogin = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    // Aplicar scope de Acumin
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        return () => {
            document.body.classList.remove('acumin-scope');
        };
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            console.log('Iniciando proceso de login...');
            const user = await login(formData.email, formData.password);

            // Esperar un tick para asegurar que el estado se propague
            await new Promise(resolve => setTimeout(resolve, 100));

            console.log('Login completado, usuario:', user);
            console.log('Navegando al home...');
            navigate('/'); // Redirige al Home después del login exitoso
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Error al iniciar sesión';
            console.error('Error en login:', errorMessage);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center pt-24 pb-12 px-4 sm:px-6 lg:px-8"
            style={{
                backgroundImage: 'url(/images/backgrounds/hero-radial-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="w-full max-w-md">
                {/* Card de Login */}
                <div
                    className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                    style={{
                        backgroundColor: '#122436',
                        border: '2px solid rgba(255,255,255,0.55)'
                    }}
                >
                    <div className="px-6 sm:px-8 py-8">
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white text-center mb-6 uppercase tracking-wider">
                            Iniciar Sesión
                        </h2>

                        {error && (
                            <div
                                className="mb-6 rounded-lg p-4 text-sm"
                                style={{
                                    backgroundColor: 'rgba(253, 2, 3, 0.15)',
                                    border: '2px solid #FD0203',
                                    color: '#FFB3B3'
                                }}
                            >
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* Email Input */}
                            <div>
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-bold text-white/90 mb-2 uppercase tracking-wide"
                                >
                                    Email
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg bg-[#0E2238] border-2 border-white/20 
                                             text-white placeholder-white/40
                                             focus:border-[#2284DF] focus:outline-none focus:ring-2 focus:ring-[#2284DF]/50
                                             transition-all"
                                    placeholder="tu@email.com"
                                />
                            </div>

                            {/* Password Input */}
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-bold text-white/90 mb-2 uppercase tracking-wide"
                                >
                                    Contraseña
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg bg-[#0E2238] border-2 border-white/20 
                                             text-white placeholder-white/40
                                             focus:border-[#2284DF] focus:outline-none focus:ring-2 focus:ring-[#2284DF]/50
                                             transition-all"
                                    placeholder="••••••••"
                                />
                                <div className="flex justify-end mt-2">
                                    <Link
                                        to="/forgot-password"
                                        className="text-sm text-[#FCE503] hover:text-[#FCE503]/80 font-semibold transition-colors"
                                    >
                                        ¿Olvidaste tu contraseña?
                                    </Link>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-2">
                                <GamingButton
                                    type="submit"
                                    variant="ctaYellow"
                                    className="w-full text-base uppercase"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                                </GamingButton>
                            </div>
                        </form>

                        {/* Divider */}
                        <div className="my-6 relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t-2 border-white/20"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-[#122436] text-white/60 font-semibold uppercase tracking-wider">
                                    o
                                </span>
                            </div>
                        </div>

                        {/* Google Login */}
                        <div className="mb-6">
                            <GoogleLoginButton />
                        </div>

                        {/* Register Link */}
                        <div className="text-center">
                            <p className="text-sm text-white/70">
                                ¿No tenés una cuenta?{' '}
                                <Link
                                    to="/register"
                                    className="font-bold text-[#FCE503] hover:text-[#FCE503]/80 transition-colors uppercase tracking-wide"
                                >
                                    Registrate ahora
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Back to home link */}
                <div className="mt-6 text-center">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors font-semibold"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Volver al inicio
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default NewUILogin;